import { findAdjacentFeedItem } from "@/utils/overlayNavigation";
import { getVisualMedia } from "@/utils/postPresentation";

const VIDEO_URL_RE = /(\.(mp4|webm|mov|m4v))(\?|#|$)/i;
const AUDIO_URL_RE = /(\.(mp3|wav|ogg|m4a|aac|flac|wma))(\?|#|$)/i;
const DOCUMENT_URL_RE =
  /(\.(pdf|doc|docx|xls|xlsx|ppt|pptx|txt|md|csv|json))(\?|#|$)/i;
const DEFAULT_CACHE_LIMIT = 48;

const normalizeUrl = (value) =>
  typeof value === "string" ? value.trim() : "";

const firstUrl = (...values) => {
  for (const value of values.flat()) {
    const url = normalizeUrl(value);
    if (url) return url;
  }
  return "";
};

const isImageCandidate = (url) =>
  Boolean(url) &&
  !VIDEO_URL_RE.test(url) &&
  !AUDIO_URL_RE.test(url) &&
  !DOCUMENT_URL_RE.test(url);

const getVariantCover = (item) => {
  const variants = item?.variants || item?.imageVariants;
  if (!variants || typeof variants !== "object") return "";
  return firstUrl(
    variants.md,
    variants.lg,
    variants.sm,
    variants.xl,
    Object.values(variants),
  );
};

const getDesignatedImage = (item) => {
  const images = Array.isArray(item?.images) ? item.images : [];
  const thumbnailIndex = Number.isInteger(item?.thumbnailIndex)
    ? item.thumbnailIndex
    : 0;
  const designated = normalizeUrl(images[thumbnailIndex]);
  if (isImageCandidate(designated)) return designated;
  return images.map(normalizeUrl).find(isImageCandidate) || "";
};

const getHeroVisual = (item) => {
  const visualMedia = getVisualMedia(item);
  if (!visualMedia.length) return null;

  const designatedImage = getDesignatedImage(item);
  if (designatedImage) {
    const designatedVisual = visualMedia.find(
      (entry) => entry.type === "image" && entry.url === designatedImage,
    );
    if (designatedVisual) return designatedVisual;
  }

  return visualMedia[0];
};

const getCoverImage = (item, heroVisual) => {
  const thumbnails = Array.isArray(item?.mediaThumbnails)
    ? item.mediaThumbnails
    : [];
  const candidates = [
    heroVisual?.poster,
    getDesignatedImage(item),
    item?.thumbnail,
    item?.thumbnailUrl,
    item?.poster,
    item?.posterUrl,
    item?.cover,
    item?.coverUrl,
    item?.coverImage,
    getVariantCover(item),
    thumbnails.find(Boolean),
  ];

  return candidates.map(normalizeUrl).find(isImageCandidate) || "";
};

/**
 * Return only the assets that can make the adjacent overlay item appear sooner.
 * Embedded players and documents are deliberately excluded from speculative loads.
 */
export function collectOverlayPreviewAssets(item) {
  if (!item || typeof item !== "object") return [];

  const mediaType = String(item.mediaType || "").trim().toLowerCase();
  const heroVisual = getHeroVisual(item);
  const assets = [];
  const seen = new Set();
  const add = (type, candidate) => {
    const url = normalizeUrl(candidate);
    const key = `${type}:${url}`;
    if (!url || seen.has(key) || DOCUMENT_URL_RE.test(url)) return;
    seen.add(key);
    assets.push({ type, url });
  };

  const isAudioItem =
    mediaType === "audio" ||
    (!mediaType && Boolean(item.audioUrl) && heroVisual?.type !== "video");

  if (isAudioItem) {
    add("image", getCoverImage(item, heroVisual));
    add("audio", firstUrl(item.mediaUrl, item.audioUrl));
    return assets;
  }

  if (heroVisual?.type === "video") {
    add("image", getCoverImage(item, heroVisual));
    add("video", heroVisual.url);
    return assets;
  }

  if (heroVisual?.type === "image") {
    add("image", heroVisual.url);
    return assets;
  }

  // Text, document, and embed items may still have a safe authored thumbnail.
  add("image", getCoverImage(item, null));
  return assets;
}

export function shouldSkipHeavyOverlayPreload(connection) {
  if (!connection) return false;
  if (connection.saveData) return true;
  return /^(slow-)?2g$/i.test(String(connection.effectiveType || ""));
}

const defaultCreateImage = () =>
  typeof Image === "function" ? new Image() : null;

const defaultCreateMediaElement = (type) =>
  typeof document !== "undefined" ? document.createElement(type) : null;

const defaultGetConnection = () =>
  typeof navigator !== "undefined" ? navigator.connection : null;

/**
 * Create a bounded, best-effort preloader. The returned methods never gate
 * navigation: assigning a source starts the browser request and returns.
 */
export function createOverlayMediaPreloader({
  maxEntries = DEFAULT_CACHE_LIMIT,
  createImage = defaultCreateImage,
  createMediaElement = defaultCreateMediaElement,
  getConnection = defaultGetConnection,
} = {}) {
  const cache = new Map();
  const cacheLimit = Math.max(1, Number(maxEntries) || DEFAULT_CACHE_LIMIT);

  const remember = (key, resource) => {
    if (cache.has(key)) return false;
    while (cache.size >= cacheLimit) {
      cache.delete(cache.keys().next().value);
    }
    cache.set(key, resource || true);
    return true;
  };

  const preloadAsset = (asset) => {
    const key = `${asset.type}:${asset.url}`;
    if (cache.has(key)) return;

    try {
      if (asset.type === "image") {
        const image = createImage();
        if (!image || !remember(key, image)) return;
        image.decoding = "async";
        image.fetchPriority = "low";
        image.src = asset.url;
        return;
      }

      if (shouldSkipHeavyOverlayPreload(getConnection())) return;
      const media = createMediaElement(asset.type);
      if (!media || !remember(key, media)) return;
      media.preload = "metadata";
      if (asset.type === "video") media.muted = true;
      media.src = asset.url;
      media.load?.();
    } catch {
      // Preloading is an optional latency optimization; navigation must proceed.
    }
  };

  const preloadItemPreview = (item) => {
    collectOverlayPreviewAssets(item).forEach(preloadAsset);
  };

  const preloadAdjacentItems = (items, currentId) => {
    const previous = findAdjacentFeedItem(items, currentId, "prev");
    const next = findAdjacentFeedItem(items, currentId, "next");
    [previous, next].filter(Boolean).forEach(preloadItemPreview);
  };

  return {
    preloadItemPreview,
    preloadAdjacentItems,
    clear: () => cache.clear(),
    get cacheSize() {
      return cache.size;
    },
  };
}

const overlayMediaPreloader = createOverlayMediaPreloader();

export const preloadAdjacentOverlayItems = ({ items, currentId }) => {
  overlayMediaPreloader.preloadAdjacentItems(items, currentId);
};
